// The correction panel of one problem: its name, the problem in a sentence or two, the solution in a few
// steps and, one level down, «Ver solución técnica» (code, steps for WordPress, WCAG references, the data
// of the element). It sits beside the capture, which marks the element that is selected; moving between
// the elements of the problem changes the capture and the panel together (the details are keyed by the
// element). Nothing here marks a problem as fixed: copying code or applying a change leaves it open until
// a check (a comparable analysis or a manual review) shows it is gone.
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircledIcon,
  CopyIcon,
  EyeOpenIcon,
} from '@radix-ui/react-icons';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Label as RacLabel, Radio, RadioGroup } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import type {
  Device,
  GuidanceProposal,
  PageIssue,
  PageOccurrence,
  ScanDetail,
  ScannedPage,
  StateTarget,
} from '../api';
import { elementInstructions } from '../element-instructions';
import { locate, type PageElement, positionOf } from '../element-logic';
import type { Problem } from '../results-logic';
import { Button, Fold } from '../ui';
import { pathOf } from '../util';
import { verificationOf } from '../verification';
import { CodeBox, useCopy } from './code-box';
import { BulkState, ElementState, type Rights } from './follow-state';
import { DEVICE_WIDTH } from './inspector';
import { PriorityChip, ProblemFollowUp } from './results-parts';
import { useDate } from './scan-parts';

/**
 * A proposed change: what kind of proposal it is, the code to put, why, and where (only if known). The code the
 * element has now is shown once above the proposals, and the reminder that nothing is fixed until a check says so
 * is said once under all of them.
 */
function ProposalView({ proposal }: { proposal: GuidanceProposal }) {
  const { t } = useTranslation();
  return (
    <div className="proposal" data-status={proposal.status}>
      <p className="proposal-kind">{t(`element.proposal.${proposal.status}`)}</p>
      <CodeBox
        code={proposal.after}
        label={t('element.proposal.proposedCode', { language: proposal.language.toUpperCase() })}
      />
      <p>{proposal.explanation}</p>
      <p className="text-sm text-ink-muted">
        {proposal.where
          ? t('element.proposal.whereKnown', { where: proposal.where })
          : t('element.proposal.whereUnknown')}
      </p>
    </div>
  );
}

/** Choosing between informative and decorative before any change is proposed for an image. */
function DecisionView({
  decision,
  proposals,
}: {
  decision: NonNullable<import('../api').Guidance['decision']>;
  proposals: GuidanceProposal[];
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<string | null>(null);
  const chosen = proposals.find((proposal) => proposal.choice === choice);
  return (
    <div className="decision">
      <RadioGroup value={choice ?? ''} onChange={setChoice} className="flex flex-col gap-2">
        <RacLabel className="font-bold">{decision.question}</RacLabel>
        <p className="text-sm text-ink-muted">{decision.hint}</p>
        {decision.options.map((option) => (
          <Radio
            key={option.id}
            value={option.id}
            className="group flex cursor-pointer gap-3 rounded-lg border border-line bg-paper p-3 data-[selected]:border-brand data-[selected]:bg-brand-tint data-[focus-visible]:outline-3 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-brand"
          >
            <span
              aria-hidden="true"
              className="mt-0.5 size-5 shrink-0 rounded-full border border-field-border group-data-[selected]:border-[6px] group-data-[selected]:border-brand"
            />
            <span>
              <span className="block font-bold">{option.label}</span>
              <span className="block text-sm text-ink-muted">{option.description}</span>
            </span>
          </Radio>
        ))}
      </RadioGroup>
      <div aria-live="polite">
        {chosen ? (
          <ProposalView proposal={chosen} />
        ) : (
          <p className="text-sm text-ink-muted">{t('element.decision.choose')}</p>
        )}
      </div>
    </div>
  );
}

/** True from the moment the value changes (never for the first one): the sentence is only read when the element changes. */
function useChangeAnnouncement(value: string): boolean {
  const first = useRef(value);
  const [changed, setChanged] = useState(false);
  useEffect(() => {
    if (value !== first.current) setChanged(true);
  }, [value]);
  return changed;
}

/**
 * The details of the selected element inside the panel. Keyed by the element and the device by its parent,
 * so nothing carries over from another one.
 */
function ElementDetails({
  problem,
  element,
  device,
  scan,
  page,
  siteId,
  rights,
  imageHeight,
  stacked,
  onShowCapture,
  elementCount,
  occurrenceIds,
  navigator,
  inCapture = true,
  bulk,
}: {
  /** False in the list of every problem of an analysis: there is no capture beside the panel, so nothing is located. */
  inCapture?: boolean;
  /** What «Aplicar estado a los N elementos» reaches when it is not the elements of this page (the whole analysis). */
  bulk?: { target: StateTarget; count: number };
  problem: Problem<PageIssue>;
  element: PageElement;
  device: Device;
  scan: ScanDetail;
  page: ScannedPage;
  siteId: string;
  rights: Rights;
  imageHeight: number | null;
  /** On a narrow screen the capture is not beside the panel: a button leads to it. */
  stacked: boolean;
  onShowCapture: () => void;
  /** How many elements the problem has on the page: the element is named when there are several. */
  elementCount: number;
  /** Every occurrence of the problem on this page: what applying one state to all the elements reaches. */
  occurrenceIds: string[];
  /** «Elemento N de M» with «Anterior» and «Siguiente», when the problem has several elements here. */
  navigator: ReactNode;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const { status, copy } = useCopy();
  const occurrence = element.byDevice[device] ?? Object.values(element.byDevice)[0];
  if (!occurrence) return null;
  const guidance = occurrence.guidance;
  const width = DEVICE_WIDTH[device];
  // The capture marks the element: that is its location. A written position only appears when it cannot be marked.
  const located = inCapture
    ? locate(occurrence, { available: page.captures[device], width, height: imageHeight })
    : ({ ok: true } as const);
  const position =
    !located.ok && occurrence.box ? positionOf(occurrence.box, { width, height: imageHeight }) : null;
  const positionText = position
    ? position.vertical !== null
      ? t('element.where.positionValue', {
          vertical: position.vertical,
          side: t(`element.where.sides.${position.side}`),
        })
      : t('element.where.positionSide', { side: t(`element.where.sides.${position.side}`) })
    : null;
  const deviceName = t(`scan.devices.${device}`);
  const captureDate = date(scan.finishedAt ?? scan.createdAt);
  // The problem, in a sentence or two: the one written for this element when there is one.
  const what = guidance?.what ?? problem.description ?? problem.title;
  const steps = guidance?.steps ?? (problem.fix ? [problem.fix] : []);
  const check = guidance?.check ?? [
    t('element.generic.check1'),
    t('element.generic.check2'),
    t('element.generic.check3'),
  ];
  const proposals = guidance?.proposals ?? [];
  const plain = proposals.filter((proposal) => !proposal.choice);
  const owners = problem.roles.map((role) => t(`element.roles.${role}`));
  const first = plain[0] ?? proposals[0];
  // The element's own code, once; the proposal's «before» only when the element has none.
  const currentCode = occurrence.html || first?.before || '';

  const instructions = () =>
    elementInstructions({
      t: (key, vars) => t(key, vars) as string,
      problem: {
        title: problem.title,
        priority: t(`results.priority.${problem.priority}`),
        criteria: problem.criteria,
      },
      element: { title: element.name.title, zone: element.name.zoneLabel },
      page: { title: page.title || pathOf(page.finalUrl), url: page.finalUrl },
      device: deviceName,
      date: captureDate,
      what,
      evidence: guidance?.evidence ?? [],
      steps,
      check,
      code: first
        ? { status: first.status, before: first.before, after: first.after, explanation: first.explanation }
        : null,
      selector: element.selector,
      html: occurrence.html,
      position: positionText,
    });

  const verification = verificationOf(occurrence.follow);
  const stateHint = `${t(`follow.work.statuses.${occurrence.follow.work.status}`)} · ${t(`follow.panel.kinds.${verification.kind}`)}`;

  return (
    <>
      {/* The way through: the problem, the element it is about, how to solve it, where the work stands and what a
          check found. The technical solution is one level down. */}
      <section className="panel-section" aria-labelledby={`problema-${element.id}`}>
        <h4 id={`problema-${element.id}`}>{t('element.panel.problem')}</h4>
        <p>{what}</p>
      </section>

      {elementCount > 0 && (
        <section className="panel-section" aria-labelledby={`elemento-${element.id}`}>
          <h4 id={`elemento-${element.id}`}>{t('element.panel.element')}</h4>
          <p className="panel-element">
            <strong>{element.name.title}</strong>
            {element.name.zoneLabel && <span className="text-ink-muted"> — {element.name.zoneLabel}</span>}
          </p>
          {navigator}
          {stacked && (
            <Button id="ver-captura" variant="secondary" onPress={onShowCapture} className="self-start">
              <EyeOpenIcon aria-hidden="true" />
              {t('element.panel.showWhere')}
            </Button>
          )}
          {!located.ok && (
            <p className="panel-notice" role="status">
              {t(`inspector.notLocated.${located.reason}`)}
            </p>
          )}
          {occurrence.sharedWith && (
            <GlobalElement
              shared={occurrence.sharedWith}
              problemKey={problem.key}
              scanId={scan.id}
              siteId={siteId}
              pages={scan.pages}
            />
          )}
        </section>
      )}

      <section className="panel-section" aria-labelledby={`solucion-${element.id}`}>
        <h4 id={`solucion-${element.id}`}>{t('element.panel.solution')}</h4>
        {steps.length > 1 ? (
          <ol className="guide-steps">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        ) : (
          steps[0] && <p>{steps[0]}</p>
        )}
      </section>

      <Fold className="panel-technical" title={t('element.panel.technical')}>
        <div className="panel-technical-body">
          {/* The code the element has now, once; the change proposed follows it. */}
          {currentCode && <CodeBox code={currentCode} label={t('element.proposal.currentCode')} />}
          {guidance?.decision ? (
            <DecisionView decision={guidance.decision} proposals={proposals} />
          ) : (
            plain.map((proposal) => <ProposalView key={proposal.after} proposal={proposal} />)
          )}
          {proposals.length > 0 && (
            <p className="text-sm text-ink-muted">{t('element.proposal.notAGuarantee')}</p>
          )}
          {guidance?.cms && (
            <div className="guide-cms">
              <p className="font-bold">{guidance.cms.label}</p>
              <ol className="guide-steps">
                {guidance.cms.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>
          )}
          {problem.criteriaDetail.length > 0 && (
            <div>
              <p className="font-bold">{t('element.panel.wcag')}</p>
              <ul>
                {problem.criteriaDetail.map((criterion) => (
                  <li key={criterion.id}>
                    {t('results.detail.criterion', {
                      id: criterion.id,
                      title: criterion.title,
                      level: criterion.level ?? '',
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <dl className="problem-technical">
            {guidance && guidance.evidence.length > 0 && (
              <div>
                <dt>{t('element.panel.measured')}</dt>
                <dd>{guidance.evidence.map((line) => `${line.label}: ${line.value}`).join(' · ')}</dd>
              </div>
            )}
            <div>
              <dt>{t('element.sections.owner')}</dt>
              <dd>{owners.length > 0 ? owners.join(' · ') : t('element.roles.unknown')}</dd>
            </div>
            {positionText && (
              <div>
                <dt>{t('element.technical.position')}</dt>
                <dd>{positionText}</dd>
              </div>
            )}
            {occurrence.message && (
              <div>
                <dt>{t('results.detail.engineMessage')}</dt>
                <dd>
                  <span lang={problem.ruleId.startsWith('axe:') ? 'en' : 'es'}>{occurrence.message}</span>
                </dd>
              </div>
            )}
          </dl>
          {/* Where to find it in the code: secondary, for whoever needs it. */}
          <details className="panel-location">
            <summary>{t('element.technical.location')}</summary>
            <div className="tech-code">
              <CodeBox code={element.selector} label={t('element.technical.selector')} />
              {occurrence.xpath && <CodeBox code={occurrence.xpath} label={t('element.technical.xpath')} />}
            </div>
          </details>
          <div className="guide-copy">
            <Button variant="secondary" onPress={() => void copy(instructions())}>
              <CopyIcon aria-hidden="true" />
              {t('element.copy.action')}
            </Button>
            <p role="status" className="text-sm">
              {status === 'copied'
                ? t('element.copy.copied')
                : status === 'failed'
                  ? t('element.copy.failed')
                  : ''}
            </p>
          </div>
        </div>
      </Fold>
      <Fold className="panel-follow" title={t('follow.title')} hint={stateHint}>
        <ElementState
          scanId={scan.id}
          siteId={siteId}
          element={{ scanId: scan.id, occurrenceId: occurrence.id }}
          state={occurrence.follow}
          result={problem.result}
          devices={element.devices}
          mayTrack={rights.track}
          mayDiscard={rights.discard}
          checkSteps={check}
          pageKey={page.key}
          hideTitle
          guideTitles={Object.fromEntries(
            problem.criteriaDetail.map((criterion) => [criterion.id, criterion.title]),
          )}
          extra={
            <BulkState
              scanId={scan.id}
              siteId={siteId}
              target={bulk?.target ?? { occurrenceIds: [...new Set(occurrenceIds)] }}
              count={bulk?.count ?? new Set(occurrenceIds).size}
              mayTrack={rights.track}
            />
          }
        />
      </Fold>
    </>
  );
}

/**
 * The panel of a problem: the way back to the list, its name and priority, the element it is about (with
 * «Anterior» and «Siguiente» when there are several), «Problema» and «Solución». The capture beside it
 * shows only this problem's elements.
 */
/**
 * An element that several pages repeat (a header, a footer, a menu) is one problem with one cause: it is looked after
 * from one page and what the team records about it reaches every page that has it. Each page keeps its own record,
 * and the correction is only verified when a new analysis (or a person) no longer finds it on every one of them.
 */
function GlobalElement({
  shared,
  problemKey,
  scanId,
  siteId,
  pages,
}: {
  shared: NonNullable<PageOccurrence['sharedWith']>;
  problemKey: string;
  scanId: string;
  siteId: string;
  pages: ScannedPage[];
}) {
  const { t } = useTranslation();
  const nameOf = (key: string, url: string | null) =>
    pages.find((page) => page.key === key)?.title || (url ? pathOf(url) : key);
  const total = shared.members.length;
  const owner = nameOf(shared.owner.pageKey, shared.owner.url);
  return (
    <details className="panel-shared" open>
      {/* This one element, not the whole problem: the problem can affect more pages than this component. */}
      <summary>{t('element.shared.title', { count: total })}</summary>
      <p className="text-sm">
        {shared.owner.isThisPage
          ? t('element.shared.here', { count: total })
          : t('element.shared.there', { page: owner, count: total })}
      </p>
      {!shared.owner.isThisPage && (
        <p className="text-sm">
          <Link
            to="/sites/$siteId/paginas"
            params={{ siteId }}
            search={{ scan: scanId, p: shared.owner.pageKey, k: problemKey }}
            className="wrap-anywhere"
          >
            {t('element.shared.open', { page: owner })}
          </Link>
        </p>
      )}
      <p className="text-sm font-bold">{t(`element.shared.progress.${shared.progress}`)}</p>
      <p className="text-sm">
        {t(shared.identical ? 'element.shared.identical' : 'element.shared.textChanges')}
      </p>
      <ul>
        {shared.members.map((member) => (
          <li key={member.pageKey}>
            <Link
              to="/sites/$siteId/paginas"
              params={{ siteId }}
              search={{ scan: scanId, p: member.pageKey }}
              className="wrap-anywhere"
            >
              {member.url ? pathOf(member.url) : member.pageKey}
            </Link>
            {member.lang && <span className="text-ink-muted"> · {member.lang}</span>}
            <span className="text-ink-muted">
              {' · '}
              {t(`follow.work.statuses.${member.work ?? 'pendiente'}`)}
              {' · '}
              {member.check ? t(`follow.check.results.${member.check}`) : t('follow.check.none')}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-sm">{t('element.shared.separately')}</p>
    </details>
  );
}

/**
 * The solution of a problem, in the one order everywhere in SiteNorma: the problem, the element it is about (with the
 * move between the elements), the solution, «Ver solución técnica» and, last, «Estado de la incidencia» (both closed).
 * It is the content of the panel beside the capture in «Páginas» and of the problem opened in «Ver todos los
 * problemas»: the same component, so a problem reads the same from either.
 */
export function ProblemSolution({
  problem,
  elements,
  selectedId,
  onSelect,
  device,
  scan,
  page,
  siteId,
  rights,
  imageHeight,
  stacked,
  onShowCapture,
  inCapture = true,
  bulk,
  analysisElements,
}: {
  problem: Problem<PageIssue>;
  elements: PageElement[];
  /** The element chosen (any of its occurrence ids); the first one when none is. */
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  device: Device;
  scan: ScanDetail;
  page: ScannedPage;
  siteId: string;
  rights: Rights;
  imageHeight: number | null;
  stacked: boolean;
  onShowCapture: () => void;
  inCapture?: boolean;
  bulk?: { target: StateTarget; count: number };
  /** Elements of this problem across the whole analysis, when the panel is opened from the scan results. */
  analysisElements?: number;
}) {
  const { t } = useTranslation();
  const current = elements.find((element) => element.occurrenceIds.includes(selectedId ?? '')) ?? elements[0];
  const shownDevice = current
    ? current.devices.includes(device)
      ? device
      : (current.devices[0] ?? device)
    : device;
  return current ? (
    <ElementDetails
      // Another element or device starts the details from scratch: nothing is carried over.
      key={`${current.id}-${shownDevice}`}
      problem={problem}
      element={current}
      device={shownDevice}
      scan={scan}
      page={page}
      siteId={siteId}
      rights={rights}
      imageHeight={imageHeight}
      stacked={stacked}
      onShowCapture={onShowCapture}
      inCapture={inCapture}
      bulk={bulk}
      elementCount={elements.length}
      occurrenceIds={elements.flatMap((element) => element.occurrenceIds)}
      navigator={
        elements.length > 1 ? (
          <ElementNavigator
            elements={elements}
            selectedId={current.id}
            total={problem.elements}
            analysisTotal={analysisElements}
            onSelect={onSelect}
          />
        ) : null
      }
    />
  ) : (
    <>
      <section className="panel-section">
        <h4>{t('element.panel.problem')}</h4>
        <p>{problem.description ?? problem.title}</p>
      </section>
      <section className="panel-section">
        <h4>{t('element.panel.solution')}</h4>
        <p>{problem.fix ?? t('element.nav.noElements')}</p>
      </section>
      <ProblemFollowUp
        problem={problem}
        rights={rights}
        siteId={siteId}
        scanId={scan.id}
        occurrenceIds={[]}
      />
    </>
  );
}

export function CorrectionPanel({
  problem,
  elements,
  selectedId,
  onSelect,
  onBack,
  device,
  scan,
  page,
  siteId,
  rights,
  imageHeight,
  stacked,
  onShowCapture,
}: {
  problem: Problem<PageIssue>;
  elements: PageElement[];
  /** The element chosen (any of its occurrence ids); the first one when none is. */
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  onBack: () => void;
  device: Device;
  scan: ScanDetail;
  page: ScannedPage;
  siteId: string;
  rights: Rights;
  imageHeight: number | null;
  stacked: boolean;
  onShowCapture: () => void;
}) {
  const { t } = useTranslation();
  const heading = useRef<HTMLHeadingElement>(null);
  // Opening a problem takes the focus to its name, so a screen reader starts where the panel does.
  useEffect(() => heading.current?.focus(), []);
  return (
    <section className="correction-panel" aria-labelledby="panel-titulo">
      <Button variant="secondary" onPress={onBack} className="panel-back">
        <ArrowLeftIcon aria-hidden="true" />
        {t('element.panel.back')}
      </Button>
      <header className="panel-head">
        <h3 id="panel-titulo" ref={heading} tabIndex={-1} className="focus:outline-none">
          {problem.title}
        </h3>
        <PriorityChip priority={problem.priority} />
      </header>
      <ProblemSolution
        problem={problem}
        elements={elements}
        selectedId={selectedId}
        onSelect={onSelect}
        device={device}
        scan={scan}
        page={page}
        siteId={siteId}
        rights={rights}
        imageHeight={imageHeight}
        stacked={stacked}
        onShowCapture={onShowCapture}
      />
    </section>
  );
}

/** Moving between the elements of a problem: where we are, «Anterior» and «Siguiente». */
export function ElementNavigator({
  elements,
  selectedId,
  total,
  analysisTotal,
  onSelect,
}: {
  elements: PageElement[];
  selectedId: string;
  /** How many elements the problem has on this page (more than the engine kept for locating). */
  total: number;
  /**
   * How many the same problem has across the whole analysis. When it is higher than this page's, the
   * line says so, so «19 elementos» on the card and «1 de 7 en esta página» here do not look like a
   * contradiction.
   */
  analysisTotal?: number;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const index = Math.max(
    0,
    elements.findIndex((element) => element.id === selectedId),
  );
  const announcement = useChangeAnnouncement(selectedId);
  const previous = elements[index - 1];
  const next = elements[index + 1];
  const shown = elements[index];
  // Reaching the first or the last element disables the button that was pressed, and a disabled button cannot
  // keep the focus: it goes to the other one, so a keyboard keeps its place instead of falling to the page.
  const previousButton = useRef<HTMLButtonElement>(null);
  const nextButton = useRef<HTMLButtonElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a change of element can disable the pressed button.
  useEffect(() => {
    const active = document.activeElement;
    if (active && active !== document.body && !(active as HTMLButtonElement).disabled) return;
    const lost = active === document.body || (active as HTMLButtonElement)?.disabled;
    if (lost && elements.length > 1) (next ? nextButton : previousButton).current?.focus();
  }, [index]);
  // With one element there is nothing to move through.
  if (elements.length < 2) return null;
  const pageTotal = Math.max(total, elements.length);
  const acrossAnalysis = analysisTotal != null && analysisTotal > pageTotal;
  return (
    <nav className="element-nav" aria-label={t('element.nav.label')}>
      <p className="element-nav-position">
        <strong>{t('element.nav.position', { index: index + 1, total: elements.length })}</strong>
        {total > elements.length && (
          <span className="text-sm text-ink-muted">
            {' '}
            · {t('element.nav.sample', { shown: elements.length, total })}
          </span>
        )}
        {acrossAnalysis && (
          <span className="text-sm text-ink-muted">
            {' '}
            · {t('element.nav.analysisTotal', { count: analysisTotal })}
          </span>
        )}
      </p>
      <div className="element-nav-buttons">
        <Button
          ref={previousButton}
          variant="secondary"
          onPress={() => previous && onSelect(previous.id)}
          isDisabled={!previous}
        >
          <ArrowLeftIcon aria-hidden="true" />
          {t('element.nav.previous')}
        </Button>
        <Button
          ref={nextButton}
          variant="secondary"
          onPress={() => next && onSelect(next.id)}
          isDisabled={!next}
        >
          {t('element.nav.next')}
          <ArrowRightIcon aria-hidden="true" />
        </Button>
      </div>
      {/* One polite sentence when the element changes: where we are and which one it is. */}
      <p role="status" className="sr-only">
        {announcement && shown
          ? t('element.nav.status', { index: index + 1, total: elements.length, name: shown.name.title })
          : ''}
      </p>
    </nav>
  );
}
